import React, { useMemo, useState } from "react";

import { formatCurrency } from "./dashboardUtils";

export default function BranchPerformanceTable({
  inventoryData,
  purchaseData,
  thresholdData,
  isLoading,
}) {
  const [activeTab, setActiveTab] = useState("all"); // "all" | "inventory" | "purchase" | "threshold"
  const [search, setSearch] = useState("");

  const fontStyle = { fontFamily: "'Inter', sans-serif" };

  const inventoryStores = inventoryData?.stores || [];
  const purchaseStores = purchaseData?.stores || [];
  const thresholdStores = thresholdData?.stores || [];

  // Ghép dữ liệu chuẩn theo từng chi nhánh từ 3 response API
  const mergedStores = useMemo(() => {
    const storeMap = new Map();

    inventoryStores.forEach((item) => {
      const code = item.store_code;
      if (!code) return;
      storeMap.set(code, {
        store_code: code,
        store_name: item.store_name || code,
        inventory_amount: Number(item.total_amount) || 0,
        purchase_amount: 0,
        threshold_weekday: 0,
        threshold_weekend: 0,
        threshold_holiday_weekday: 0,
        threshold_holiday_weekend: 0,
      });
    });

    purchaseStores.forEach((item) => {
      const code = item.store_code;
      if (!code) return;
      const current = storeMap.get(code) || {
        store_code: code,
        store_name: item.store_name || code,
        inventory_amount: 0,
        purchase_amount: 0,
        threshold_weekday: 0,
        threshold_weekend: 0,
        threshold_holiday_weekday: 0,
        threshold_holiday_weekend: 0,
      };
      current.purchase_amount = Number(item.total_amount) || 0;
      if (!current.store_name && item.store_name) {
        current.store_name = item.store_name;
      }
      storeMap.set(code, current);
    });

    thresholdStores.forEach((item) => {
      const code = item.store_code;
      if (!code) return;
      const current = storeMap.get(code) || {
        store_code: code,
        store_name: item.store_name || code,
        inventory_amount: 0,
        purchase_amount: 0,
        threshold_weekday: 0,
        threshold_weekend: 0,
        threshold_holiday_weekday: 0,
        threshold_holiday_weekend: 0,
      };
      current.threshold_weekday = Number(item.weekday_normal_amount) || 0;
      current.threshold_weekend = Number(item.weekend_normal_amount) || 0;
      current.threshold_holiday_weekday = Number(item.weekday_holiday_amount) || 0;
      current.threshold_holiday_weekend = Number(item.weekend_holiday_amount) || 0;
      if (!current.store_name && item.store_name) {
        current.store_name = item.store_name;
      }
      storeMap.set(code, current);
    });

    return Array.from(storeMap.values());
  }, [inventoryStores, purchaseStores, thresholdStores]);

  // Lọc tìm kiếm
  const filteredStores = useMemo(() => {
    let list = [...mergedStores];
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (s) =>
          (s.store_code && s.store_code.toLowerCase().includes(q)) ||
          (s.store_name && s.store_name.toLowerCase().includes(q))
      );
    }
    return list;
  }, [mergedStores, search]);

  const totalInventory = inventoryData?.total_amount ?? 0;
  const totalPurchase = purchaseData?.total_amount ?? 0;
  const totalThresholdWeekday = thresholdData?.weekday_normal_amount ?? 0;
  const totalThresholdWeekend = thresholdData?.weekend_normal_amount ?? 0;
  const totalThresholdHolidayWeekday = thresholdData?.weekday_holiday_amount ?? 0;
  const totalThresholdHolidayWeekend = thresholdData?.weekend_holiday_amount ?? 0;

  return (
    <div
      className="!font-inter !bg-white !rounded-2xl !p-4 sm:!p-5 !shadow-xs !border !border-slate-200/90 !space-y-4"
      style={fontStyle}
    >
      {/* Header & Search */}
      <div className="!flex !flex-col sm:!flex-row !items-start sm:!items-center !justify-between !gap-3" style={fontStyle}>
        <div style={fontStyle}>
          <h3 className="!text-base sm:!text-lg !font-extrabold !text-slate-800 !leading-tight" style={fontStyle}>
            Số Liệu Chi Tiết Theo Chi Nhánh
          </h3>
          <p className="!text-sm !font-medium !text-slate-500 !mt-0.5" style={fontStyle}>
            Dữ liệu thống kê theo chi nhánh
          </p>
        </div>

        {/* Search Input */}
        <div className="!relative !w-full sm:!w-72" style={fontStyle}>
          <input
            type="text"
            placeholder="Tìm theo mã hoặc tên chi nhánh..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="!w-full !text-sm !bg-slate-50/80 focus:!bg-white !border !border-slate-300 !rounded-xl !px-3.5 !py-2 !text-slate-700 placeholder:!text-slate-400 focus:!outline-none focus:!border-blue-500 focus:!ring-2 focus:!ring-blue-100 !transition-all"
            style={fontStyle}
          />
        </div>
      </div>

      {/* Segmented Tabs */}
      <div className="!flex !flex-wrap !gap-2 !border-b !border-slate-200 !pb-2.5" style={fontStyle}>
        <button
          onClick={() => setActiveTab("all")}
          className={`!px-3.5 !py-1.5 !rounded-xl !text-sm !font-bold !transition-all ${
            activeTab === "all"
              ? "!bg-[#053c7f] !text-white !shadow-xs"
              : "!bg-slate-100/90 !text-slate-600 hover:!bg-slate-200 hover:!text-slate-800"
          }`}
          style={fontStyle}
        >
          Tổng hợp ({filteredStores.length})
        </button>
        <button
          onClick={() => setActiveTab("inventory")}
          className={`!px-3.5 !py-1.5 !rounded-xl !text-sm !font-bold !transition-all ${
            activeTab === "inventory"
              ? "!bg-[#053c7f] !text-white !shadow-xs"
              : "!bg-slate-100/90 !text-slate-600 hover:!bg-slate-200 hover:!text-slate-800"
          }`}
          style={fontStyle}
        >
          Tồn kho ({inventoryStores.length})
        </button>
        <button
          onClick={() => setActiveTab("purchase")}
          className={`!px-3.5 !py-1.5 !rounded-xl !text-sm !font-bold !transition-all ${
            activeTab === "purchase"
              ? "!bg-[#053c7f] !text-white !shadow-xs"
              : "!bg-slate-100/90 !text-slate-600 hover:!bg-slate-200 hover:!text-slate-800"
          }`}
          style={fontStyle}
        >
          Mua hàng ({purchaseStores.length})
        </button>
        <button
          onClick={() => setActiveTab("threshold")}
          className={`!px-3.5 !py-1.5 !rounded-xl !text-sm !font-bold !transition-all ${
            activeTab === "threshold"
              ? "!bg-[#053c7f] !text-white !shadow-xs"
              : "!bg-slate-100/90 !text-slate-600 hover:!bg-slate-200 hover:!text-slate-800"
          }`}
          style={fontStyle}
        >
          Định mức tồn kho ({thresholdStores.length})
        </button>
      </div>

      {/* Bảng dữ liệu thuần (Headers 1 dòng whitespace-nowrap, cuộn ngang) */}
      <div className="!overflow-x-auto !border !border-slate-200/90 !rounded-xl !max-h-[540px] !shadow-2xs" style={fontStyle}>
        <table className="!w-full !text-left !border-collapse !text-sm" style={fontStyle}>
          <thead className="!bg-slate-50 !sticky !top-0 !z-10 !border-b !border-slate-200" style={fontStyle}>
            <tr className="!text-slate-700 !font-bold !whitespace-nowrap" style={fontStyle}>
              {/* Mã Chi Nhánh căn giữa */}
              <th className="!py-3.5 !px-4 !text-center !w-36 !whitespace-nowrap !tracking-wide" style={fontStyle}>
                Mã Chi Nhánh
              </th>
              <th className="!py-3.5 !px-4 !min-w-[190px] !whitespace-nowrap !tracking-wide" style={fontStyle}>
                Tên Chi Nhánh
              </th>

              {(activeTab === "all" || activeTab === "inventory") && (
                <th className="!py-3.5 !px-4 !min-w-[170px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                  Tiền Tồn Kho
                </th>
              )}

              {(activeTab === "all" || activeTab === "purchase") && (
                <th className="!py-3.5 !px-4 !min-w-[170px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                  Tiền Mua Hàng
                </th>
              )}

              {(activeTab === "all" || activeTab === "threshold") && (
                <>
                  <th className="!py-3.5 !px-4 !min-w-[180px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                    Định Mức Ngày Thường
                  </th>
                  <th className="!py-3.5 !px-4 !min-w-[180px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                    Định Mức Cuối Tuần
                  </th>
                  <th className="!py-3.5 !px-4 !min-w-[190px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                    Định Mức Lễ Ngày Thường
                  </th>
                  <th className="!py-3.5 !px-4 !min-w-[190px] !text-right !whitespace-nowrap !tracking-wide" style={fontStyle}>
                    Định Mức Lễ Cuối Tuần
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="!divide-y !divide-slate-100 !bg-white" style={fontStyle}>
            {isLoading ? (
              <tr style={fontStyle}>
                <td colSpan="8" className="!py-14 !text-center !text-slate-400 !whitespace-nowrap" style={fontStyle}>
                  Đang tải dữ liệu chi nhánh...
                </td>
              </tr>
            ) : filteredStores.length === 0 ? (
              <tr style={fontStyle}>
                <td colSpan="8" className="!py-12 !text-center !text-slate-400 !whitespace-nowrap" style={fontStyle}>
                  Không tìm thấy chi nhánh nào.
                </td>
              </tr>
            ) : (
              filteredStores.map((item) => (
                <tr
                  key={item.store_code}
                  className="hover:!bg-slate-50/80 !transition-colors !whitespace-nowrap"
                  style={fontStyle}
                >
                  {/* Mã Chi Nhánh căn giữa */}
                  <td className="!py-3 !px-4 !text-center !whitespace-nowrap" style={fontStyle}>
                    <span className="!inline-block !px-2.5 !py-0.5 !bg-slate-100 !text-slate-700 !rounded-md !font-mono !font-semibold !text-sm">
                      {item.store_code}
                    </span>
                  </td>

                  <td className="!py-3 !px-4 !font-bold !text-slate-800 !whitespace-nowrap" style={fontStyle}>
                    {item.store_name}
                  </td>

                  {(activeTab === "all" || activeTab === "inventory") && (
                    <td className="!py-3 !px-4 !text-right !font-bold !text-blue-700 !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(item.inventory_amount)}
                    </td>
                  )}

                  {(activeTab === "all" || activeTab === "purchase") && (
                    <td className="!py-3 !px-4 !text-right !font-bold !text-emerald-700 !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(item.purchase_amount)}
                    </td>
                  )}

                  {(activeTab === "all" || activeTab === "threshold") && (
                    <>
                      <td className="!py-3 !px-4 !text-right !font-semibold !text-slate-700 !whitespace-nowrap" style={fontStyle}>
                        {formatCurrency(item.threshold_weekday)}
                      </td>
                      <td className="!py-3 !px-4 !text-right !font-semibold !text-slate-700 !whitespace-nowrap" style={fontStyle}>
                        {formatCurrency(item.threshold_weekend)}
                      </td>
                      <td className="!py-3 !px-4 !text-right !font-semibold !text-slate-700 !whitespace-nowrap" style={fontStyle}>
                        {formatCurrency(item.threshold_holiday_weekday)}
                      </td>
                      <td className="!py-3 !px-4 !text-right !font-semibold !text-slate-700 !whitespace-nowrap" style={fontStyle}>
                        {formatCurrency(item.threshold_holiday_weekend)}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {filteredStores.length > 0 && (
            <tfoot className="!bg-slate-50 !font-bold !border-t-2 !border-slate-300 !whitespace-nowrap" style={fontStyle}>
              <tr style={fontStyle}>
                <td colSpan="2" className="!py-3.5 !px-4 !text-slate-700 !whitespace-nowrap" style={fontStyle}>
                  Tổng cộng:
                </td>

                {(activeTab === "all" || activeTab === "inventory") && (
                  <td className="!py-3.5 !px-4 !text-right !text-blue-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                    {formatCurrency(totalInventory)}
                  </td>
                )}

                {(activeTab === "all" || activeTab === "purchase") && (
                  <td className="!py-3.5 !px-4 !text-right !text-emerald-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                    {formatCurrency(totalPurchase)}
                  </td>
                )}

                {(activeTab === "all" || activeTab === "threshold") && (
                  <>
                    <td className="!py-3.5 !px-4 !text-right !text-slate-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(totalThresholdWeekday)}
                    </td>
                    <td className="!py-3.5 !px-4 !text-right !text-slate-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(totalThresholdWeekend)}
                    </td>
                    <td className="!py-3.5 !px-4 !text-right !text-slate-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(totalThresholdHolidayWeekday)}
                    </td>
                    <td className="!py-3.5 !px-4 !text-right !text-slate-800 !font-black !text-sm !whitespace-nowrap" style={fontStyle}>
                      {formatCurrency(totalThresholdHolidayWeekend)}
                    </td>
                  </>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
